import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload } from "lucide-react";

const MATERIALS = [
  "Acier inoxydable 304 plaqué or",
  "Acier inoxydable 316 plaqué or",
  "Acier inoxydable 304",
  "Acier inoxydable 316",
  "Création artisanale",
];

const CATEGORIES = ["chaines", "bracelets", "boucles", "ensembles", "chaussures", "accessoires"];
const AUDIENCES = ["femme", "homme", "enfant", "unisexe"];

const EMPTY = {
  sku: "",
  name: "",
  category: "chaines",
  audience: "femme",
  material: MATERIALS[0],
  price: 0,
  cost_price: 0,
  stock: 0,
  spiritual_description: "",
  photos: [],
  is_new: true,
  is_favorite: false,
};

function countWords(text) {
  return (text || "").trim().split(/\s+/).filter(Boolean).length;
}

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/products/${id}`).then((r) => {
      const p = r.data;
      // Need cost_price too - fetch from admin list (cost_price is hidden from public)
      api.get(`/admin/products`).then((rr) => {
        const full = rr.data.find((x) => x.id === id);
        setForm({ ...EMPTY, ...p, ...full });
      });
    });
  }, [id, isEdit]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (form.photos.length + files.length > 10) {
      toast.error("Maximum 10 photos par produit");
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: crypto.randomUUID(), path: res.data.path, order: form.photos.length + uploaded.length });
      }
      setForm({ ...form, photos: [...form.photos, ...uploaded] });
      toast.success("Photos téléversées");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (i) => {
    const arr = form.photos.filter((_, idx) => idx !== i);
    setForm({ ...form, photos: arr.map((p, idx) => ({ ...p, order: idx })) });
  };

  const movePhoto = (from, to) => {
    if (to < 0 || to >= form.photos.length) return;
    const arr = [...form.photos];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    setForm({ ...form, photos: arr.map((p, idx) => ({ ...p, order: idx })) });
  };

  const save = async () => {
    if (!form.name || form.price <= 0) {
      toast.error("Nom et prix requis");
      return;
    }
    if (countWords(form.spiritual_description) > 60) {
      toast.error("La description ne doit pas dépasser 60 mots");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, price: Number(form.price), cost_price: Number(form.cost_price), stock: Number(form.stock) };
      if (isEdit) {
        await api.put(`/admin/products/${id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produit créé");
      }
      navigate("/admin/produits");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-3xl lg:text-4xl mb-2">{isEdit ? "Modifier le produit" : "Nouveau produit"}</h1>
      <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mb-10">Formulaire produit complet</p>

      <div className="space-y-6" data-testid="product-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-tiny block mb-1">Code produit (SKU)</label>
            <input
              className="input-gold"
              value={form.sku || ""}
              onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
              placeholder="Ex : RDF-CH-001"
              data-testid="product-sku"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">Nom *</label>
            <input className="input-gold" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" />
          </div>
          <div>
            <label className="label-tiny block mb-1">Matière</label>
            <select className="input-gold" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })}>
              {MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny block mb-1">Catégorie</label>
            <select className="input-gold" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny block mb-1">Public</label>
            <select className="input-gold" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
              {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny block mb-1">Prix de vente (FCFA) *</label>
            <input type="number" className="input-gold" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price" />
          </div>
          <div>
            <label className="label-tiny block mb-1">Prix de revient (FCFA)</label>
            <input type="number" className="input-gold" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="product-cost" />
          </div>
          <div>
            <label className="label-tiny block mb-1">Stock</label>
            <input type="number" className="input-gold" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} />
              Marquer comme Nouveauté
            </label>
          </div>
          <div className="flex items-end md:col-span-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="favorite-toggle-label">
              <input
                type="checkbox"
                checked={form.is_favorite}
                onChange={(e) => setForm({ ...form, is_favorite: e.target.checked })}
                data-testid="product-favorite-checkbox"
              />
              <span className="text-[#B8860B]">★ Coup de cœur du mois</span>
              <span className="text-xs text-[#6B6B6B] italic">— mise en avant éditoriale</span>
            </label>
          </div>
        </div>

        <div>
          <label className="label-tiny block mb-1">
            {`Description (max 60 mots) — texte spirituel et inspirant`}
            <span className={`ml-3 ${countWords(form.spiritual_description) > 60 ? "text-red-400" : "text-[#6B6B6B]"}`}>
              {countWords(form.spiritual_description)} / 60
            </span>
          </label>
          <textarea
            className="input-gold min-h-[120px]"
            value={form.spiritual_description}
            onChange={(e) => setForm({ ...form, spiritual_description: e.target.value })}
            data-testid="product-spiritual-desc"
            placeholder="Une parole inspirante qui invite à porter cette pièce avec foi..."
          />
        </div>

        {/* Photos */}
        <div>
          <label className="label-tiny block mb-3">{`Photos (${form.photos.length}/10) — la première est l'image principale`}</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            {form.photos.map((p, i) => (
              <div key={p.id || i} className="relative border border-black/10 aspect-square">
                <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <div className="absolute top-1 left-1 bg-[#B8860B] text-[#0A0A0A] text-[9px] px-1.5 py-0.5 uppercase tracking-widest">
                    Principale
                  </div>
                )}
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/80 text-white p-1" data-testid={`remove-photo-${i}`}>
                  <X size={12} />
                </button>
                <div className="absolute bottom-1 left-1 right-1 flex justify-between">
                  <button type="button" onClick={() => movePhoto(i, i - 1)} className="bg-black/80 text-white text-[10px] px-1.5">←</button>
                  <button type="button" onClick={() => movePhoto(i, i + 1)} className="bg-black/80 text-white text-[10px] px-1.5">→</button>
                </div>
              </div>
            ))}
            <label className="border border-dashed border-black/15 aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-[#B8860B]" data-testid="upload-photo-btn">
              <Upload size={20} className="text-[#6B6B6B]" />
              <span className="text-[10px] uppercase tracking-widest text-[#6B6B6B] mt-2">{uploading ? "Envoi..." : "Ajouter"}</span>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" />
            </label>
          </div>
        </div>

        {/* Variants section removed */}

        <div className="flex gap-4 pt-6 border-t border-black/5">
          <button className="btn-secondary" onClick={() => navigate("/admin/produits")}>Annuler</button>
          <button className="btn-primary flex-1" onClick={save} disabled={loading} data-testid="save-product-btn">
            {loading ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer le produit"}
          </button>
        </div>
      </div>
    </div>
  );
}
